import React, { useEffect, useRef, useCallback, useState } from 'react';
import {
  CROSS_FIELD_NAV_OPEN_EVENT,
  CROSS_FIELD_NAV_CLOSE_EVENT,
  getCrossFieldNavContext,
  closeCrossFieldNav,
  type CrossFieldNavContext
} from '../utils/crossFieldNavBus';

/**
 * CrossFieldNavigationMenu - Event-driven menu with React-controlled DOM
 * ✅ SAFE: Uses event bus + local state (NO createElement, NO innerHTML)
 * ✅ NO PARENT RE-RENDERS: EditFilings doesn't re-render, only menu updates
 * ✅ React-owned nodes: All DOM is JSX, not imperative manipulation
 */
export const CrossFieldNavigationMenu: React.FC = () => {
  const menuRootRef = useRef<HTMLDivElement>(null);
  const [menuContext, setMenuContext] = useState<CrossFieldNavContext | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  
  // Open handler - just update local state
  const handleOpen = useCallback(() => {
    const ctx = getCrossFieldNavContext();
    if (!ctx) return;
    
    const { currentLocation, allLocations } = ctx;
    const otherLocations = allLocations.filter(loc => loc.sectionId !== currentLocation.sectionId);
    if (otherLocations.length === 0) return;
    
    setMenuContext(ctx);
    setIsOpen(true);
  }, []);
  
  // Close handler - just update local state
  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);
  
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isOpen && menuRootRef.current && !menuRootRef.current.contains(e.target as Node)) {
        closeCrossFieldNav();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);
  
  // Event listeners
  useEffect(() => {
    document.addEventListener(CROSS_FIELD_NAV_OPEN_EVENT, handleOpen);
    document.addEventListener(CROSS_FIELD_NAV_CLOSE_EVENT, handleClose);
    
    return () => {
      document.removeEventListener(CROSS_FIELD_NAV_OPEN_EVENT, handleOpen);
      document.removeEventListener(CROSS_FIELD_NAV_CLOSE_EVENT, handleClose);
    };
  }, [handleOpen, handleClose]);
  
  if (!isOpen || !menuContext) return null;
  
  const { currentLocation, allLocations, triggerButtonRect, fieldName, onNavigate } = menuContext;
  const otherLocations = allLocations.filter(loc => loc.sectionId !== currentLocation.sectionId);
  
  return (
    <div
      ref={menuRootRef}
      className="cross-field-nav-menu"
      style={{
        position: 'fixed',
        top: `${triggerButtonRect.bottom + 5}px`,
        left: `${triggerButtonRect.left}px`
      }}
    >
      <div className="cross-field-nav-menu-header">
        "{currentLocation.fieldLabel}" also appears in:
      </div>
      
      {otherLocations.map((location) => (
        <div
          key={location.sectionId}
          className="cross-field-nav-menu-item"
          onClick={() => {
            onNavigate(location.sectionId, fieldName);
            closeCrossFieldNav();
          }}
        >
          <div className="cross-field-nav-menu-item-title">
            {location.sectionName}
          </div>
        </div>
      ))}
    </div>
  );
};
